import React from 'react'
import type { Block, Variable } from '../editor/types'
import { useBlockAnimation } from './useBlockAnimation'
import TextBlock from './blocks/TextBlock'
import SliderBlock from './blocks/SliderBlock'
import ChartBlock from './blocks/ChartBlock'
import ButtonBlock from './blocks/ButtonBlock'
import ImageBlock from './blocks/ImageBlock'
import StepsBlock from './blocks/StepsBlock'
import ChatBubbleBlock from './blocks/ChatBubbleBlock'
import CodeBlock from './blocks/CodeBlock'
import ToggleBlock from './blocks/ToggleBlock'
import DropdownBlock from './blocks/DropdownBlock'
import DividerBlock from './blocks/DividerBlock'
import VideoBlock from './blocks/VideoBlock'
import TabsBlock from './blocks/TabsBlock'
import NumberInputBlock from './blocks/NumberInputBlock'
import CompareBlock from './blocks/CompareBlock'
import PollBlock from './blocks/PollBlock'
import QuizBlock from './blocks/QuizBlock'

interface BlockRendererProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
  demoId?: number
}

function BlockContent({ block, variableValues, variables, onSetVariableValue, demoId }: BlockRendererProps) {
  switch (block.type) {
    case 'text':
      return <TextBlock block={block} variableValues={variableValues} />
    case 'slider':
      return <SliderBlock block={block} variableValues={variableValues} variables={variables} onSetVariableValue={onSetVariableValue} />
    case 'chart':
      return <ChartBlock block={block} variableValues={variableValues} variables={variables} />
    case 'button':
      return <ButtonBlock block={block} variableValues={variableValues} onSetVariableValue={onSetVariableValue} />
    case 'image':
      return <ImageBlock block={block} />
    case 'steps':
      return <StepsBlock block={block} variableValues={variableValues} onSetVariableValue={onSetVariableValue} />
    case 'chat_bubble':
      return <ChatBubbleBlock block={block} variableValues={variableValues} />
    case 'codeblock':
      return <CodeBlock block={block} />
    case 'toggle':
      return <ToggleBlock block={block} variableValues={variableValues} variables={variables} onSetVariableValue={onSetVariableValue} />
    case 'dropdown':
      return <DropdownBlock block={block} variableValues={variableValues} variables={variables} onSetVariableValue={onSetVariableValue} />
    case 'divider':
      return <DividerBlock block={block} />
    case 'video':
      return <VideoBlock block={block} />
    case 'tabs':
      return <TabsBlock block={block} variableValues={variableValues} variables={variables} onSetVariableValue={onSetVariableValue} />
    case 'number_input':
      return <NumberInputBlock block={block} variableValues={variableValues} variables={variables} onSetVariableValue={onSetVariableValue} />
    case 'compare':
      return <CompareBlock block={block} />
    case 'poll':
      return <PollBlock block={block} demoId={demoId} onSetVariableValue={onSetVariableValue} />
    case 'quiz':
      return <QuizBlock block={block} demoId={demoId} onSetVariableValue={onSetVariableValue} />
    default:
      return <div className="text-text-secondary text-sm">Unknown block type: {block.type}</div>
  }
}

function getBlockBgStyle(bg?: Block['background']): React.CSSProperties {
  if (!bg) return {}
  switch (bg.type) {
    case 'solid':
      return { backgroundColor: bg.value }
    case 'gradient':
      return { background: bg.value }
    case 'image':
      return { backgroundImage: `url(${bg.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    case 'animated':
      return {}
    default:
      return {}
  }
}

function getBlockBgClass(bg?: Block['background']): string {
  if (!bg || bg.type !== 'animated') return ''
  return bg.value
}

export default function BlockRenderer(props: BlockRendererProps) {
  const { block, variableValues } = props
  const { ref, className, style } = useBlockAnimation({
    animation: block.animation,
    variableValues,
  })

  const hasBg = !!block.background
  const bgStyle = getBlockBgStyle(block.background)
  const bgClass = getBlockBgClass(block.background)

  const blockStyle: React.CSSProperties = {
    ...style,
    ...(block.accent ? { '--accent': block.accent } as React.CSSProperties : {}),
  }

  if (!hasBg) {
    return (
      <div ref={ref} className={className} style={blockStyle}>
        <BlockContent {...props} />
      </div>
    )
  }

  return (
    <div ref={ref} className={className} style={blockStyle}>
      <div
        className={`rounded-xl p-4 overflow-hidden relative ${bgClass}`}
        style={bgStyle}
      >
        <div className="relative z-10">
          <BlockContent {...props} />
        </div>
      </div>
    </div>
  )
}
